import { FeatureFlagName } from '@/components/session/session.store';

export type FeatureFlagUsage = 'client' | 'backend' | 'both';

interface FeatureFlagConfig {
  description: string;
  usage: FeatureFlagUsage;
}

export const FEATURE_FLAGS_CONFIG: Record<FeatureFlagName, FeatureFlagConfig> = {
  // Client-only flags (UI features, frontend behavior)
  BATCH_ESG_ANALYSIS_PDF_EXPORT: {
    description: 'Show the batch One-Pager export buttons in the portfolio and transaction analysis headers',
    usage: 'client',
  },
  BUILDING_CREATION_CONSUMER_EXTENSION: {
    description: 'Allow consumer extension in building creation',
    usage: 'client',
  },
  BUILDING_CREATION_EPC_LIMITLESS: {
    description: 'Remove EPC limits in building creation',
    usage: 'client',
  },
  BUILDING_MODEL_DRAFT: {
    description: 'Enable building model draft functionality',
    usage: 'client',
  },
  BUILDING_MODEL_HISTORY: {
    description: 'Enable building model history functionality',
    usage: 'client',
  },
  CBI_ENABLED: {
    description: 'Enable CBI (Consumption Bill Invoices) features',
    usage: 'client',
  },
  CLIENT_CACHE_AND_NETWORK_POLICY: {
    description: 'Enable client cache and network policy',
    usage: 'client',
  },
  CLIMATE_RISK_V2: {
    description: 'Enable Climate Risk V2 features',
    usage: 'client',
  },
  CLIMATE_RISK_V2_MOCK: {
    description: 'Use mock data for Climate Risk V2',
    usage: 'client',
  },
  COMPARE_WITH_USE_TYPE_OPTIONS: {
    description: 'Show same class and type of use entries in the building "Compare with" dropdown',
    usage: 'client',
  },
  CONFIGURABLE_CO2_PRICE: {
    description: 'Allow configuring the organisation CO₂ price (€/t) development curve',
    usage: 'client',
  },
  EU_TAXONOMY_DATASOURCE: {
    description: 'Show the EU Taxonomy data source selector in organisation settings',
    usage: 'client',
  },
  ENABLE_DREES_UND_SOMMER: {
    description: 'Offer the Drees & Sommer data source as an option for EU Taxonomy compliance',
    usage: 'both',
  },
  HEATING_DEMAND_EDITABLE: {
    description: 'Allow editing of heating demand',
    usage: 'client',
  },
  HIDE_CBI_INVOICE_FIELDS: {
    description: 'Hide certain CBI invoice fields in the UI',
    usage: 'client',
  },
  HIDE_CBI_INTERNAL_COMMENTS: {
    description: 'Hide snapshot comments from internal users (@predium.de) in CBI history',
    usage: 'client',
  },
  LOGIN_HISTORY: {
    description: 'Show login activity section on user profile',
    usage: 'client',
  },
  M2_ABSOLUTE_VALUE_SWITCHER_FOR_ACTION_PLANNING: {
    description: 'Switch action-planning charts/KPIs between absolute values and relative (delta) values',
    usage: 'both',
  },
  MAPS_PROXY: {
    description: 'Use maps proxy for enterprise clients with restricted network access',
    usage: 'client',
  },
  MULTI_SELECT_IN_DRIVE_FEATURE: {
    description: 'Enable multi-file selection and bulk building-draft creation in Drive',
    usage: 'client',
  },
  NEW_BUILDINGS_TABLE: {
    description: 'Render the buildings list with the new table data layer (fetching machine + PTable)',
    usage: 'client',
  },
  NEW_CONSTRUCTION: {
    description: 'Enable new construction building support (planned buildings with future year_constructed)',
    usage: 'client',
  },
  NO_COMPARISON_BENCHMARK: {
    description: 'Add "No comparison" as the default option in the analysis benchmark "Compare with" selector',
    usage: 'client',
  },
  EXPERIMENTAL_BULK_ACTION_FEATURES: {
    description: 'Enable experimental bulk action features',
    usage: 'client',
  },
  CUSTOM_EXCEL_EXPORT: {
    description: 'Show the “Custom export” option in the bulk export dialog (column picker)',
    usage: 'client',
  },
  CUSTOM_PROPERTIES: {
    description: 'Enable custom properties (Zusatzattribute): management, display, and analysis-header fields',
    usage: 'both',
  },
  CUSTOM_PROPERTY_EDITING: {
    description:
      'Allow editing custom properties in the UI — building values + org-settings management. Read-only when off (API editing is unaffected)',
    usage: 'client',
  },
  ACTION_PLAN_BULK_DELETION: {
    description: 'Enable bulk selection and deletion of action plans in the scenario action-plan table',
    usage: 'client',
  },
  ACTION_PLAN_PORTFOLIO_MISMATCH_HIGHLIGHT: {
    description: 'Enable action plan portfolio mismatch highlight',
    usage: 'client',
  },
  REPORTING_MENU_OPTION_ENABLED: {
    description: 'Show reporting menu option',
    usage: 'client',
  },
  BE_COUNTRY_SUPPORT: {
    description: 'Enable Belgium as a selectable country (end-to-end support: regions, CRREM, BPI, translations, flag)',
    usage: 'both',
  },
  CZ_COUNTRY_SUPPORT: {
    description:
      'Enable Czech Republic as a selectable country (end-to-end support: regions, CRREM, BPI, translations, flag)',
    usage: 'both',
  },
  FRANCE_COUNTRY_SUPPORT: {
    description: 'Enable France as a selectable country (end-to-end support: regions, CRREM, BPI, translations, flag)',
    usage: 'both',
  },
  NL_COUNTRY_SUPPORT: {
    description:
      'Enable the Netherlands as a selectable country (end-to-end support: regions, CRREM, BPI, translations, flag)',
    usage: 'both',
  },
  UK_COUNTRY_SUPPORT: {
    description:
      'Enable the United Kingdom as a selectable country (end-to-end support: regions, CRREM, BPI, translations, flag)',
    usage: 'both',
  },

  // Backend-only flags (processing, calculations, API behavior)
  AUTO_DEACTIVATE_INACTIVE_USERS: {
    description: 'Automatically deactivate users who have not logged in for more than 30 days',
    usage: 'backend',
  },
  KYSELY_ANALYZE_SCENARIO: {
    description: 'Route scenario analysis endpoints through the Kysely implementation instead of Prisma',
    usage: 'backend',
  },
  OUT_OF_RANGE_GRADIENT_DESCENT_SCALING: {
    description: 'Enable out of range gradient descent scaling',
    usage: 'backend',
  },

  // Both client and backend (features that require coordination)
  GLB_MODEL_ACCESS: {
    description: 'Allow access to GLB models',
    usage: 'both', // Client access + Backend permissions
  },
  CUSTOMIZABLE_CALCULATIONS: {
    description: 'Reference area selector in consumption status quo analysis',
    usage: 'client',
  },
  ARBEITSHILFE_85_EMISSION_FACTORS: {
    description: 'Offer the Arbeitshilfe 85 emission factor set in analysis settings',
    usage: 'client',
  },
  CONSUMPTION_HUB: {
    description: 'Show the Consumption hub page (nested under Data Collection)',
    usage: 'client',
  },
  CONSUMPTION_PORTFOLIO_ANALYSIS: {
    description: 'Enable Consumption Analysis on Portfolio / Economic Unit level',
    usage: 'client',
  },
  DISABLE_NON_RESIDENTIAL_EFFICIENCY_CLASSES: {
    description: 'Disable efficiency class display and calculations for non-residential buildings',
    usage: 'both',
  },
  DRIVE_BULK_CREATE_BUILDINGS: {
    description: 'Automatically create buildings with default values after bulk-converting drive files to EPCs',
    usage: 'both',
  },
  SCOPE_PERSPECTIVE_SELECTOR: {
    description: 'Allow users to change scope perspective',
    usage: 'both',
  },
  SET_BASED_EXPORT: {
    description: 'Excel exports use the set-based analyzer instead of the per-building legacy path',
    usage: 'backend',
  },
  SLF_CHECK_COMPLIANT: {
    description: 'Show SLF check compliant alert',
    usage: 'client',
  },
  USER_MANAGEMENT_CREATE: {
    description: 'Enable user creation in user management',
    usage: 'client',
  },
  SHOW_ORG_LOGO_IN_SIDEBAR: {
    description: 'Show org logo in sidebar',
    usage: 'client',
  },
  USE_CUSTOM_TARGET_PATH: {
    description: 'Use custom target path for status quo analysis',
    usage: 'both',
  },
  ENABLE_DECARBONISATION_PATH_IN_ACTION_PLANNING: {
    description:
      'Use the CRREM-style action-planning chart (dropdown + CRREM sub-tabs + custom decarbonisation path) instead of the legacy flat chart selector',
    usage: 'client',
  },
  ENABLE_SHAREABLE_BUILDING_STATUS_QUO: {
    description: 'Allow creating and sharing public read-only links to building status quo analysis',
    usage: 'both',
  },
  ENABLE_STATUS_QUO_MIRRORING_EXPORT: {
    description:
      'Show the "Detailed" PDF variant in the status-quo export dialog. The Detailed PDF mirrors the current Demand or Consumption tab view.',
    usage: 'both',
  },
  ENERGY_NETWORKS: {
    description:
      'Enable Energy Networks (district heating and electricity emission factors) instead of Energy Suppliers',
    usage: 'both',
  },
  BUILDING_MODEL_VERSIONS_ANALYSIS: {
    description: 'Enable building model versions analysis',
    usage: 'both',
  },
  APPRAISAL_REVIEW_ENABLED: {
    description:
      'Show the Dokumente → Posteingang review inbox with its appraisal review UI, and the Transaktion list and detail page that replace the economic-unit accordion for banking orgs',
    usage: 'client',
  },
};
